// ForwardsDealerDashboard.js - Forwards Dealer Dashboard
// URL-path-based tabs following BankFXPartnerDashboard pattern
// FX forwards trading: RFQs, settlement, contracts book, position limits, analytics, audit

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TrendingUp, DollarSign, ArrowRightLeft, Globe, RefreshCw,
  Search, CheckCircle, Clock, AlertTriangle, Activity,
  FileText, BarChart3, Eye, Calendar, Percent
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { useAuth } from '../../context/AuthContext';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

// ============================================
// DEMO / FALLBACK DATA
// ============================================
const demoData = {
  metrics: {
    activeRfqs: 9,
    openContracts: 34,
    totalNotional: 126500000,
    mtmPnl: 1843200
  },
  rfqs: [
    { id: 'RFQ-4001', investor: 'INV-AA12', pair: 'USD/KES', notional: 5000000, tenor: '3M', maturityDate: '2026-12-15', status: 'open', deadline: '2026-09-13T16:00:00Z' },
    { id: 'RFQ-4002', investor: 'INV-BB34', pair: 'EUR/KES', notional: 3200000, tenor: '6M', maturityDate: '2027-03-10', status: 'open', deadline: '2026-09-13T12:00:00Z' },
    { id: 'RFQ-4003', investor: 'INV-CC56', pair: 'GBP/KES', notional: 1800000, tenor: '1M', maturityDate: '2026-10-12', status: 'submitted', deadline: '2026-09-12T18:00:00Z' },
    { id: 'RFQ-4004', investor: 'INV-DD78', pair: 'USD/UGX', notional: 7500000, tenor: '12M', maturityDate: '2027-09-12', status: 'open', deadline: '2026-09-14T10:00:00Z' },
    { id: 'RFQ-4005', investor: 'INV-EE90', pair: 'EUR/TZS', notional: 2400000, tenor: '3M', maturityDate: '2026-12-08', status: 'expired', deadline: '2026-09-10T14:00:00Z' },
    { id: 'RFQ-4006', investor: 'INV-FF23', pair: 'USD/KES', notional: 10000000, tenor: '6M', maturityDate: '2027-03-15', status: 'open', deadline: '2026-09-15T09:00:00Z' },
    { id: 'RFQ-4007', investor: 'INV-GG45', pair: 'GBP/UGX', notional: 900000, tenor: '1M', maturityDate: '2026-10-14', status: 'accepted', deadline: '2026-09-11T16:00:00Z' },
    { id: 'RFQ-4008', investor: 'INV-HH67', pair: 'USD/TZS', notional: 4200000, tenor: '9M', maturityDate: '2027-06-10', status: 'open', deadline: '2026-09-14T15:00:00Z' },
    { id: 'RFQ-4009', investor: 'INV-JJ89', pair: 'EUR/KES', notional: 6800000, tenor: '3M', maturityDate: '2026-12-20', status: 'open', deadline: '2026-09-15T11:00:00Z' },
  ],
  settlements: [
    { id: 'FWD-3001', investor: 'INV-AA12', marginAmount: 500000, currency: 'USD', dueDate: '2026-09-15', status: 'pending' },
    { id: 'FWD-3002', investor: 'INV-BB34', marginAmount: 320000, currency: 'EUR', dueDate: '2026-09-14', status: 'confirmed' },
    { id: 'FWD-3003', investor: 'INV-CC56', marginAmount: 180000, currency: 'GBP', dueDate: '2026-09-16', status: 'pending' },
    { id: 'FWD-3004', investor: 'INV-DD78', marginAmount: 750000, currency: 'USD', dueDate: '2026-09-13', status: 'flagged' },
    { id: 'FWD-3005', investor: 'INV-EE90', marginAmount: 240000, currency: 'EUR', dueDate: '2026-09-17', status: 'confirmed' },
    { id: 'FWD-3006', investor: 'INV-FF23', marginAmount: 1000000, currency: 'USD', dueDate: '2026-09-18', status: 'pending' },
  ],
  contracts: [
    { id: 'FWD-3001', pair: 'USD/KES', direction: 'Buy', notional: 5000000, strikeRate: 129.85, valueDate: '2026-09-12', maturityDate: '2026-12-15', mtm: 125400, status: 'active' },
    { id: 'FWD-3002', pair: 'EUR/KES', direction: 'Sell', notional: 3200000, strikeRate: 142.10, valueDate: '2026-09-10', maturityDate: '2027-03-10', mtm: -42300, status: 'active' },
    { id: 'FWD-3003', pair: 'GBP/KES', direction: 'Buy', notional: 1800000, strikeRate: 165.20, valueDate: '2026-09-08', maturityDate: '2026-10-12', mtm: 31200, status: 'active' },
    { id: 'FWD-3004', pair: 'USD/UGX', direction: 'Buy', notional: 7500000, strikeRate: 3720.50, valueDate: '2026-09-12', maturityDate: '2027-09-12', mtm: 287600, status: 'active' },
    { id: 'FWD-3005', pair: 'EUR/TZS', direction: 'Sell', notional: 2400000, strikeRate: 2845.00, valueDate: '2026-09-05', maturityDate: '2026-12-08', mtm: -18900, status: 'matured' },
    { id: 'FWD-3006', pair: 'USD/KES', direction: 'Buy', notional: 10000000, strikeRate: 130.15, valueDate: '2026-09-11', maturityDate: '2027-03-15', mtm: 412500, status: 'active' },
    { id: 'FWD-3007', pair: 'GBP/UGX', direction: 'Sell', notional: 900000, strikeRate: 4820.00, valueDate: '2026-09-09', maturityDate: '2026-10-14', mtm: 15200, status: 'active' },
    { id: 'FWD-3008', pair: 'USD/TZS', direction: 'Buy', notional: 4200000, strikeRate: 2530.80, valueDate: '2026-09-07', maturityDate: '2027-06-10', mtm: -67400, status: 'active' },
    { id: 'FWD-3009', pair: 'EUR/KES', direction: 'Buy', notional: 6800000, strikeRate: 141.90, valueDate: '2026-09-12', maturityDate: '2026-12-20', mtm: 198300, status: 'active' },
    { id: 'FWD-3010', pair: 'USD/KES', direction: 'Sell', notional: 8500000, strikeRate: 129.50, valueDate: '2026-08-20', maturityDate: '2027-02-20', mtm: 901500, status: 'active' },
  ],
  positionBuckets: [
    { bucket: '0-1M', long: 6700000, short: 3200000, net: 3500000, limit: 15000000, utilization: 23.3 },
    { bucket: '1-3M', long: 21800000, short: 12400000, net: 9400000, limit: 30000000, utilization: 31.3 },
    { bucket: '3-6M', long: 13200000, short: 10500000, net: 2700000, limit: 25000000, utilization: 10.8 },
    { bucket: '6-12M', long: 11700000, short: 4200000, net: 7500000, limit: 20000000, utilization: 37.5 },
    { bucket: '12M+', long: 0, short: 0, net: 0, limit: 10000000, utilization: 0 },
  ],
  volumeTrend: [
    { month: 'Apr', volume: 18200000, count: 14 },
    { month: 'May', volume: 22500000, count: 18 },
    { month: 'Jun', volume: 28100000, count: 22 },
    { month: 'Jul', volume: 24800000, count: 19 },
    { month: 'Aug', volume: 31400000, count: 26 },
    { month: 'Sep', volume: 26500000, count: 21 },
  ],
  tenorDistribution: [
    { name: '0-1M', value: 12 },
    { name: '1-3M', value: 28 },
    { name: '3-6M', value: 24 },
    { name: '6-12M', value: 18 },
    { name: '12M+', value: 4 },
  ],
  mtmTrend: [
    { month: 'Apr', pnl: 420000 },
    { month: 'May', pnl: 780000 },
    { month: 'Jun', pnl: 1250000 },
    { month: 'Jul', pnl: 980000 },
    { month: 'Aug', pnl: 1560000 },
    { month: 'Sep', pnl: 1843200 },
  ],
  auditLog: [
    { timestamp: '2026-09-12T14:32:00Z', action: 'Quote Submitted', user: 'J. Kamau', details: 'RFQ-4003 — USD/KES 3M bid 129.80 / offer 130.05' },
    { timestamp: '2026-09-12T13:15:00Z', action: 'Margin Confirmed', user: 'J. Kamau', details: 'FWD-3002 — EUR 320,000 margin received' },
    { timestamp: '2026-09-12T11:48:00Z', action: 'Contract Booked', user: 'S. Ochieng', details: 'FWD-3009 — EUR/KES 6.8M 3M forward at 141.90' },
    { timestamp: '2026-09-12T10:05:00Z', action: 'Position Limit Updated', user: 'S. Ochieng', details: '3-6M bucket limit changed from $20M to $25M' },
    { timestamp: '2026-09-11T16:22:00Z', action: 'RFQ Declined', user: 'J. Kamau', details: 'RFQ-4005 — EUR/TZS 3M declined (market conditions)' },
    { timestamp: '2026-09-11T14:10:00Z', action: 'Settlement Flagged', user: 'M. Wanjiku', details: 'FWD-3004 — USD 750,000 margin overdue, investor notified' },
    { timestamp: '2026-09-11T09:30:00Z', action: 'Quote Submitted', user: 'J. Kamau', details: 'RFQ-4007 — GBP/UGX 1M bid 4815.00 / offer 4825.00' },
    { timestamp: '2026-09-10T15:45:00Z', action: 'Contract Matured', user: 'System', details: 'FWD-3005 — EUR/TZS 2.4M settled at maturity' },
  ],
};

const CHART_COLORS = ['#3832e3', '#4D4D4D', '#7b7eff', '#a1a1aa', '#c6ccff', '#d4d4d8'];

// ============================================
// FORMAT HELPERS
// ============================================
const formatCurrency = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val?.toLocaleString() || 0}`;
};

const formatPnl = (val) => {
  const prefix = val >= 0 ? '+' : '';
  if (Math.abs(val) >= 1000000) return `${prefix}$${(val / 1000000).toFixed(2)}M`;
  if (Math.abs(val) >= 1000) return `${prefix}$${(val / 1000).toFixed(0)}K`;
  return `${prefix}$${val?.toLocaleString() || 0}`;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const formatTime = (d) => d ? new Date(d).toLocaleString() : '—';

// ============================================
// STATUS BADGE HELPER
// ============================================
const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

// ============================================
// STAT CARD COMPONENT
// ============================================
const StatCard = ({ label, value, icon: Icon, trend }) => (
  <div className="bg-white border border-zinc-200 rounded-none p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      <Icon className="w-4 h-4 text-zinc-400" />
    </div>
    <p className="text-2xl font-bold text-black">{value}</p>
    {trend && <p className="text-xs text-zinc-500 mt-1">{trend}</p>}
  </div>
);

// ============================================
// URL PATH TO TAB MAPPING
// ============================================
const getTabFromPath = (pathname) => {
  if (pathname.includes('/forwards/quotes')) return 'forwards_quotes';
  if (pathname.includes('/forwards/settlement')) return 'settlement_acceptance';
  if (pathname.includes('/forwards/contracts')) return 'contracts_book';
  if (pathname.includes('/forwards/positions')) return 'position_limits';
  if (pathname.includes('/forwards/analytics')) return 'analytics';
  if (pathname.includes('/forwards/audit')) return 'audit';
  return 'forwards_quotes';
};

// ============================================
// MAIN FORWARDS DEALER DASHBOARD
// ============================================
const ForwardsDealerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Dashboard data state
  const [metrics] = useState(demoData.metrics);
  const [rfqs] = useState(demoData.rfqs);
  const [settlements] = useState(demoData.settlements);
  const [contracts] = useState(demoData.contracts);
  const [positionBuckets] = useState(demoData.positionBuckets);
  const [volumeTrend] = useState(demoData.volumeTrend);
  const [tenorDistribution] = useState(demoData.tenorDistribution);
  const [mtmTrend] = useState(demoData.mtmTrend);
  const [auditLog] = useState(demoData.auditLog);

  // Determine active tab from URL path
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Simulate initial load
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  // ============================================
  // TABS CONFIGURATION
  // ============================================
  const tabs = [
    { id: 'forwards_quotes', label: 'Forward RFQs', path: '/bank/forwards/quotes', icon: ArrowRightLeft },
    { id: 'settlement_acceptance', label: 'Settlement', path: '/bank/forwards/settlement', icon: CheckCircle },
    { id: 'contracts_book', label: 'Contracts Book', path: '/bank/forwards/contracts', icon: FileText },
    { id: 'position_limits', label: 'Position & Limits', path: '/bank/forwards/positions', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', path: '/bank/forwards/analytics', icon: Activity },
    { id: 'audit', label: 'Audit Trail', path: '/bank/forwards/audit', icon: Eye },
  ];

  // Computed contract summaries
  const activeContracts = contracts.filter(c => c.status === 'active');
  const totalContractNotional = activeContracts.reduce((s, c) => s + c.notional, 0);
  const totalMtm = activeContracts.reduce((s, c) => s + c.mtm, 0);
  const avgTenorMonths = activeContracts.length > 0
    ? (activeContracts.reduce((s, c) => {
        const vd = new Date(c.valueDate);
        const md = new Date(c.maturityDate);
        return s + (md - vd) / (1000 * 60 * 60 * 24 * 30);
      }, 0) / activeContracts.length).toFixed(1)
    : 0;

  const handleRefresh = async () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 600);
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-zinc-600 mt-4">Loading forwards dealer dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-600 to-zinc-800 rounded-none p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-6 h-6" />
              <span className="text-zinc-200 text-sm font-medium">FORWARDS DEALER</span>
            </div>
            <h1 className="text-2xl font-bold">{user?.tenantName || 'Foreign Exchange Forwards Trading Desk'}</h1>
            <p className="text-zinc-100 mt-1">
              {metrics.activeRfqs} active RFQs • {metrics.openContracts} open contracts
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-zinc-200">Total Notional</p>
            <p className="text-3xl font-bold">{formatCurrency(metrics.totalNotional)}</p>
            <p className="text-sm text-zinc-200 mt-1">MTM P&L: {formatPnl(metrics.mtmPnl)}</p>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active RFQs" value={metrics.activeRfqs} icon={ArrowRightLeft} trend="Awaiting quote" />
        <StatCard label="Open Contracts" value={metrics.openContracts} icon={FileText} trend="Live forwards" />
        <StatCard label="Total Notional" value={formatCurrency(metrics.totalNotional)} icon={DollarSign} trend="All active contracts" />
        <StatCard label="MTM P&L" value={formatPnl(metrics.mtmPnl)} icon={TrendingUp} trend="Mark-to-market" />
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-none p-1 shadow-sm overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-zinc-600 text-white' : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-none"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ============================================ */}
      {/* FORWARDS QUOTES TAB                         */}
      {/* ============================================ */}
      {activeTab === 'forwards_quotes' && (
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Divestment Rate RFQs</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search RFQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">RFQ ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Investor</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Currency Pair</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Notional</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Maturity</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Deadline</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rfqs
                    .filter(r => !searchTerm || r.id.toLowerCase().includes(searchTerm.toLowerCase()) || r.pair.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((rfq) => (
                    <tr key={rfq.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{rfq.id}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500">{rfq.investor}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{rfq.pair}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(rfq.notional)}</td>
                      <td className="py-3 px-4 font-mono text-xs">{rfq.tenor}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(rfq.maturityDate)}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(rfq.status)}>{rfq.status}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatTime(rfq.deadline)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {rfq.status === 'open' && (
                            <>
                              <button className="px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                                Submit Quote
                              </button>
                              <button className="px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase text-zinc-400 hover:bg-zinc-50 transition-colors">
                                Decline
                              </button>
                            </>
                          )}
                          {rfq.status !== 'open' && (
                            <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                              <Eye className="w-3 h-3" /> View
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rfqs.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <ArrowRightLeft className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No active RFQs at this time.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SETTLEMENT ACCEPTANCE TAB                    */}
      {/* ============================================ */}
      {activeTab === 'settlement_acceptance' && (
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Margin / Nostro Confirmation</h3>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-zinc-400" />
                <span className="text-[10px] font-mono uppercase text-zinc-400">Margin is a contractual obligation</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Contract ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Investor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Margin Amount</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Currency</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Due Date</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{s.id}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500">{s.investor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{s.marginAmount.toLocaleString()}</td>
                      <td className="py-3 px-4 font-mono text-xs">{s.currency}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(s.dueDate)}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(s.status)}>{s.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {s.status === 'pending' && (
                            <>
                              <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                                <CheckCircle className="w-3 h-3" /> Confirm
                              </button>
                              <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase text-zinc-400 hover:bg-zinc-50 transition-colors">
                                <AlertTriangle className="w-3 h-3" /> Flag
                              </button>
                            </>
                          )}
                          {s.status !== 'pending' && (
                            <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                              <Eye className="w-3 h-3" /> View
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {settlements.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No pending settlements.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* CONTRACTS BOOK TAB                           */}
      {/* ============================================ */}
      {activeTab === 'contracts_book' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Notional" value={formatCurrency(totalContractNotional)} icon={DollarSign} trend="Active contracts" />
            <StatCard label="Contracts Active" value={activeContracts.length} icon={FileText} trend="Live forwards" />
            <StatCard label="Avg Tenor" value={`${avgTenorMonths}M`} icon={Calendar} trend="Weighted average" />
            <StatCard label="Total MTM" value={formatPnl(totalMtm)} icon={TrendingUp} trend="Mark-to-market P&L" />
          </div>

          {/* Contracts Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Live Forwards Contracts</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search contracts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Contract ID</th>
                    <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                    <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Dir</th>
                    <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Notional</th>
                    <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Strike</th>
                    <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Value Date</th>
                    <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Maturity</th>
                    <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">MTM P&L</th>
                    <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts
                    .filter(c => !searchTerm || c.id.toLowerCase().includes(searchTerm.toLowerCase()) || c.pair.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((c) => (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-3 font-mono text-xs">{c.id}</td>
                      <td className="py-3 px-3 font-mono text-xs font-bold">{c.pair}</td>
                      <td className="py-3 px-3 font-mono text-xs">{c.direction}</td>
                      <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(c.notional)}</td>
                      <td className="py-3 px-3 text-right font-mono text-xs">{c.strikeRate.toLocaleString()}</td>
                      <td className="py-3 px-3 text-xs text-zinc-500">{formatDate(c.valueDate)}</td>
                      <td className="py-3 px-3 text-xs text-zinc-500">{formatDate(c.maturityDate)}</td>
                      <td className={`py-3 px-3 text-right font-mono text-xs font-bold ${c.mtm >= 0 ? 'text-black' : 'text-red-700'}`}>
                        {formatPnl(c.mtm)}
                      </td>
                      <td className="py-3 px-3">
                        <span {...getStatusBadge(c.status)}>{c.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* POSITION & LIMITS TAB                        */}
      {/* ============================================ */}
      {activeTab === 'position_limits' && (
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Net Forward Exposure by Tenor Bucket</h3>
              <div className="flex items-center gap-2">
                <Percent className="w-4 h-4 text-zinc-400" />
                <span className="text-[10px] font-mono uppercase text-zinc-400">Configurable limits</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Bucket</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Long</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Short</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Net</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Limit</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {positionBuckets.map((b) => (
                    <tr key={b.bucket} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs font-bold">{b.bucket}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(b.long)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(b.short)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{formatCurrency(b.net)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs text-zinc-500">{formatCurrency(b.limit)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-24 bg-zinc-100 rounded-none h-2">
                            <div
                              className={`h-2 rounded-none ${b.utilization > 80 ? 'bg-red-600' : b.utilization > 50 ? 'bg-zinc-500' : 'bg-zinc-300'}`}
                              style={{ width: `${Math.min(b.utilization, 100)}%` }}
                            />
                          </div>
                          <span className={`font-mono text-xs ${b.utilization > 80 ? 'text-red-700 font-bold' : ''}`}>
                            {b.utilization.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Position Bar Chart */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Position vs Limit by Bucket</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={positionBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#a1a1aa" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '12px' }}
                  />
                  <Bar dataKey="net" fill="#3832e3" name="Net Position" />
                  <Bar dataKey="limit" fill="#e4e4e7" name="Limit" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ANALYTICS TAB                                */}
      {/* ============================================ */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Volume Trend */}
            <div className="bg-white border border-zinc-200 rounded-none p-6">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Forward Volume Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={volumeTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#a1a1aa" tickFormatter={(v) => formatCurrency(v)} />
                    <Tooltip
                      formatter={(value, name) => [
                        name === 'volume' ? formatCurrency(value) : value,
                        name === 'volume' ? 'Volume' : 'Deal Count'
                      ]}
                      contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '12px' }}
                    />
                    <Line type="monotone" dataKey="volume" stroke="#3832e3" strokeWidth={2} dot={{ r: 4, fill: '#3832e3' }} name="volume" />
                    <Line type="monotone" dataKey="count" stroke="#a1a1aa" strokeWidth={2} dot={{ r: 4, fill: '#a1a1aa' }} name="count" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Tenor Distribution */}
            <div className="bg-white border border-zinc-200 rounded-none p-6">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Tenor Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tenorDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {tenorDistribution.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* MTM P&L Trend */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">MTM P&L Over Time</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mtmTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#a1a1aa" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip
                    formatter={(value) => [formatPnl(value), 'MTM P&L']}
                    contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '12px' }}
                  />
                  <defs>
                    <linearGradient id="mtmGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3832e3" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#3832e3" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Line type="monotone" dataKey="pnl" stroke="#3832e3" strokeWidth={2} dot={{ r: 4, fill: '#3832e3' }} fill="url(#mtmGradient)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* AUDIT TRAIL TAB                              */}
      {/* ============================================ */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Audit Trail</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search audit log..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Timestamp</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Action</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">User</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog
                    .filter(a => !searchTerm || a.action.toLowerCase().includes(searchTerm.toLowerCase()) || a.details.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((entry, idx) => (
                    <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500 whitespace-nowrap">{formatTime(entry.timestamp)}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{entry.action}</td>
                      <td className="py-3 px-4 text-xs">{entry.user}</td>
                      <td className="py-3 px-4 text-xs text-zinc-600">{entry.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {auditLog.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <Eye className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No audit entries found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForwardsDealerDashboard;
